import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { formatAddress, toTitleCase } from "@utils/strings";
import { useNavigate, useParams } from "react-router-dom";

import { Heading, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type GetOrganizationsOutput,
  useBasecampClinicListingsRetrieve,
  useBasecampOrganizationsRetrieve,
} from "@api/rest/types-and-hooks";

import { AddEditClinicForm } from "./AddEditClinicForm";

export const EditClinicPage: React.FC = () => {
  const { orgId, clinicId } = useParams<{ orgId: string; clinicId: string }>();
  const navigate = useNavigate();

  const { data: orgData } = useBasecampOrganizationsRetrieve({ id: orgId }, { query: { enabled: !!orgId } });
  const org: GetOrganizationsOutput | undefined =
    orgData?.status === 200
      ? Array.isArray(orgData.data)
        ? orgData.data.find((o) => String(o.id) === orgId)
        : orgData.data
      : undefined;

  const { data, isLoading } = useBasecampClinicListingsRetrieve({ org_id: orgId }, { query: { enabled: !!orgId } });

  const clinics = data?.status === 200 ? (Array.isArray(data.data) ? data.data : [data.data]) : [];
  const clinic = clinics.find((c) => String(c.id) === clinicId);

  const initialValues = clinic
    ? {
        name: clinic.name ?? "",
        address: clinic.location
          ? formatAddress({
              addressLine1: toTitleCase(clinic.location.address_line_1) || undefined,
              addressLine2: toTitleCase(clinic.location.address_line_2 ?? "") || undefined,
              city: toTitleCase(clinic.location.city),
              state: clinic.location.state,
              zip: clinic.location.postal_code,
            })
          : "",
        phone: clinic.phone_number ?? "",
        website: clinic.website_url ?? "",
        email: clinic.contact_email ?? "",
        about: clinic.description ?? "",
        services: clinic.services_offered ?? [],
        modalities: clinic.modality_types ?? [],
        payors: clinic.payors ?? [],
      }
    : undefined;

  if (isLoading) return null;

  return (
    <div className="flex flex-col py-8 items-center bg-bg-default px-4 sm:px-6 lg:px-8">
      <div className="min-w-[70vw]">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate("/organizations")}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Organizations
          </button>
          {org?.name && (
            <>
              <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
              <button
                type="button"
                onClick={() => navigate(`/organizations/${orgId}?tab=clinic-listings`)}
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {org.name}
              </button>
            </>
          )}
          {clinic?.name && (
            <>
              <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
              <button
                type="button"
                onClick={() => navigate(`/organizations/${orgId}/clinics/${clinicId}`)}
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {clinic.name}
              </button>
            </>
          )}
        </div>

        <Heading size="medium">Edit clinic</Heading>
        <Paragraph size="small" colorType="secondary">
          Update your clinic information.
        </Paragraph>

        <AddEditClinicForm
          orgId={orgId!}
          clinicId={clinicId}
          initialValues={initialValues}
          onCancel={() => navigate(`/organizations/${orgId}/clinics/${clinicId}`)}
        />
      </div>
    </div>
  );
};
